import React, { useRef, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import styles from './OverflowableContent.styles';
import { useIsOverflow } from 'studio/helpers';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import MoreHorizIcon from '@mui/icons-material/MoreHorizRounded';
import Fade from '@mui/material/Fade';
import Popper from '@mui/material/Popper';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import Paper from '@mui/material/Paper';
import classNames from 'classnames';

const OverflowableContent = ({
  classes,
  className,
  content,
  tooltip,
  popperTitle,
  popperBoundariesElementRef,
  children
}) => {
  // the reference to the content element wrapping the children
  const contentRef = useRef();
  // the reference to the button
  const buttonRef = useRef();
  // specifies whether the content element overflows
  const isOverflow = useIsOverflow(contentRef);
  // specifies whether the popper is open
  const [open, setOpen] = useState(false);

  // returns the content, which is either specified via the 'content' or the 'children' property
  const getContent = isPopper => (content ? content(isPopper, isOverflow) : children);

  return (
    <>
      <div ref={contentRef} className={classNames(classes.content, className)}>
        {getContent(false)}
      </div>
      <div ref={buttonRef} className={classes.button}>
        {isOverflow && (
          <Tooltip title={tooltip || 'Show More'} placement="top" arrow>
            <IconButton
              size="small"
              onClick={event => {
                event.stopPropagation();
                setOpen(!open);
              }}
            >
              <MoreHorizIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>
      <Popper
        open={open}
        anchorEl={buttonRef?.current}
        placement="right"
        transition
        modifiers={[
          {
            name: 'preventOverflow',
            options: {
              boundary: popperBoundariesElementRef?.current || 'clippingParents'
            }
          }
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Fade {...TransitionProps} timeout={250}>
              <Paper className={classes.popperPaper}>
                {popperTitle && <div className={classes.popperTitle}>{popperTitle}</div>}
                <div className={classNames(className, 'popper')}>{getContent(true)}</div>
              </Paper>
            </Fade>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};

export default withStyles(styles)(OverflowableContent);
